import * as React from "react";

import { useAnalytics } from "@/providers";
import { Button, type ButtonProps } from "@/shared/foundation/primitives";
import {
  EventName,
  ComponentPropsWithEvent,
  EventProps,
} from "@/shared/services/analytics";

type RobotoButtonPropsWithEvent<T extends EventName> = ComponentPropsWithEvent<
  T,
  ButtonProps
>;

// This function type overload provides better intellisense by telling the author
// what field is missing from the event properties. Without this, it'll just say
// "Your value for eventProperties does not match the expected type."
export function RobotoButton<T extends EventName>(
  props: RobotoButtonPropsWithEvent<T>,
): JSX.Element;

/**
 * Button wrapped with Analytics
 */
export function RobotoButton(
  props: Partial<RobotoButtonPropsWithEvent<EventName>>,
): JSX.Element {
  const { trackEvent } = useAnalytics();
  const { onClick, eventName, eventProperties, ...buttonProps } = props;

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    if (eventName) {
      // The overload signature guarantees type safety at call sites.
      // The Partial<...> implementation signature erases the link between
      // eventName and eventProperties, so we suppress here.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const resolved: EventProps<typeof eventName> =
        typeof eventProperties === "function"
          ? eventProperties()
          : eventProperties;

      trackEvent(eventName, resolved);
    }
    onClick?.(event);
  };

  return (
    <Button onClick={handleClick} {...buttonProps}>
      {props.children}
    </Button>
  );
}
